/**
 * RecipientInput (DESIGN.md T11, §5 Inputs): To/Cc/Bcc field with chips, autocomplete and
 * "check names", like OWA/Outlook.
 *
 * - Chips: avatar + display name, address on hover/focus, x / Backspace / Delete removes,
 *   double-click / Enter / F2 edits, drag and drop between fields, group chips can be expanded.
 *   Unresolved, ambiguous and invalid chips are marked (destructive outline + icon + sr text).
 * - Typing: suggestions (debounced, cancellable) from the directory; ↑/↓ choose, Enter/Tab pick;
 *   Enter, Tab, `;` and `,` commit the typed text; Delete on a highlighted recent suggestion
 *   removes it from the suggestions; Esc closes the list.
 * - Paste of "Name <a@b>; c@d, …" becomes chips. Ctrl+K checks all names (Outlook).
 * - Keyboard: ← at the start of the text or Backspace in an empty field moves to the last chip;
 *   ←/→ move between chips, → after the last chip returns to the text.
 *
 * Data access comes in through `RecipientDirectory` (the app wires the platform API), labels
 * through props (i18n-free composite).
 */
import { AlertTriangleIcon, LoaderCircleIcon, PlusIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ClipboardEvent, type DragEvent, type KeyboardEvent } from 'react';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../components/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/tooltip';
import { getShortcuts, matchesChord } from '../hooks/use-shortcut';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';
import { PersonAvatar } from './person';
import {
  applyResolution,
  parseRecipientList,
  recipientFromEntry,
  isValidEmail,
  recipientFromParsed,
  recipientLabel,
  recipientText,
  type PersonEntry,
  type Recipient,
  type RecipientSource,
  type ResolveOutcome,
} from './recipients';

/** Where suggestions, names and photos come from (the app wires the platform API). */
export interface RecipientDirectory {
  suggest: (query: string, signal: AbortSignal) => Promise<readonly PersonEntry[]>;
  resolve: (entries: readonly string[]) => Promise<readonly ResolveOutcome[]>;
  removeSuggestion?: (entry: PersonEntry) => Promise<void>;
  expandGroup?: (recipient: Recipient) => Promise<readonly PersonEntry[]>;
  /** Photo URL of an address (the server answers 404 without a photo → initials). */
  photoUrl?: (email: string) => string | undefined;
  /** Optional bounded pagination; offsets come from the adapter, never inferred from filtered options. */
  suggestPage?: (query: string, signal: AbortSignal, offset: number) => Promise<RecipientSuggestionPage>;
}

export interface RecipientSuggestionPage {
  items: readonly PersonEntry[];
  nextOffset: number | null;
  capped?: boolean;
}

export interface RecipientInputLabels {
  /** Name of the suggestion list. */
  suggestions: string;
  searching: string;
  noMatches: string;
  /** Contains `{name}`. */
  remove: string;
  /** Contains `{name}`. */
  removeSuggestion: string;
  /** Contains `{name}`. */
  expandGroup: string;
  /** Status texts (tooltip + screen reader); contain `{name}`. */
  unresolved: string;
  ambiguous: string;
  invalid: string;
  pending: string;
  /** Header of the candidate menu of an ambiguous chip. */
  chooseCandidate: string;
  group: string;
  sources: Record<RecipientSource, string>;
  lookupFailed?: string;
  retry?: string;
  loadMore?: string;
  searchLimit?: string;
}

export interface RecipientInputProps {
  /** Field id for drag and drop between fields (to, cc, bcc). */
  field: string;
  /** Accessible name (a visible label may point at `id`, the text input). */
  label: string;
  /** Id of the text input (set by FormControl). */
  id?: string;
  'aria-describedby'?: string;
  value: readonly Recipient[];
  onChange: (next: Recipient[]) => void;
  directory: RecipientDirectory;
  labels: RecipientInputLabels;
  readOnly?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
  describedBy?: string;
  placeholder?: string;
  /** A chip was dropped here from another field (the parent removes it there). */
  onDropRecipient?: (recipient: Recipient, fromField: string) => void;
  /** Debounce of the suggestion lookup (ms). */
  debounceMs?: number;
  /** Existing entities only: typed/pasted text is a query and cannot become a principal. */
  selectionOnly?: boolean;
  maxRecipients?: number;
  minQueryLength?: number;
}

const DRAG_TYPE = 'application/x-platform-recipient';
const fill = (template: string, name: string): string => template.replace('{name}', name);
const isCheckNames = (event: globalThis.KeyboardEvent): boolean => getShortcuts('mail.checkNames').some((chord) => matchesChord(event, chord));

/** Resolve all pending/unresolved chips of a list (Ctrl+K, and before sending). */
export async function checkNames(list: readonly Recipient[], directory: Pick<RecipientDirectory, 'resolve'>): Promise<Recipient[]> {
  const open = list.filter((r) => r.status === 'pending' || r.status === 'unresolved');
  if (open.length === 0) return [...list];
  const outcomes = await directory.resolve(open.map((r) => r.input ?? r.name ?? r.email));
  const byId = new Map(open.map((r, i) => [r.id, outcomes[i]] as const));
  return list.map((r) => {
    const outcome = byId.get(r.id);
    return outcome ? applyResolution(r, outcome) : r;
  });
}

export function RecipientInput({
  field,
  label,
  id: inputId,
  'aria-describedby': ariaDescribedBy,
  value,
  onChange,
  directory,
  labels,
  readOnly = false,
  autoFocus,
  invalid,
  describedBy,
  placeholder,
  onDropRecipient,
  debounceMs = 120,
  selectionOnly = false,
  maxRecipients,
  minQueryLength = 1,
}: RecipientInputProps) {
  const ui = useUiStrings();
  const generatedId = useId();
  const id = inputId ?? `${generatedId}-input`;
  const listId = `${generatedId}-suggestions`;
  const inputRef = useRef<HTMLInputElement>(null);
  const chipRefs = useRef(new Map<string, HTMLButtonElement>());
  const [text, setText] = useState('');
  /** Answer of the last finished lookup and the query it belongs to. */
  const [result, setResult] = useState<{ query: string; directory?: RecipientDirectory; items: readonly PersonEntry[]; nextOffset: number | null; capped?: boolean; failed?: boolean }>({ query: '', items: [], nextOffset: null });
  const [retryKey, setRetryKey] = useState(0);
  const [moreState, setMoreState] = useState<{ query: string; directory: RecipientDirectory } | null>(null);
  const lookupVersion = useRef(0);
  const currentValue = useRef(value);
  const resolutionGeneration = useRef(0);
  useLayoutEffect(() => { currentValue.current = value; }, [value]);
  useEffect(() => {
    resolutionGeneration.current += 1;
    return () => { resolutionGeneration.current += 1; };
  }, [directory]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [dragOver, setDragOver] = useState(false);
  const query = text.trim();
  const moreLoading = moreState?.query === query && moreState.directory === directory;
  const currentResult = result.query === query && result.directory === directory;
  const loading = !readOnly && query.length >= minQueryLength && !currentResult;
  // Addresses already in the field are not suggested again.
  const taken = new Set(value.map((r) => r.email.toLowerCase()).filter(Boolean));
  const visible = currentResult ? result.items.filter((s) => !taken.has(s.email.toLowerCase())) : [];
  const setSuggestions = (update: (items: readonly PersonEntry[]) => readonly PersonEntry[]) => setResult((r) => ({ ...r, items: update(r.items) }));

  // Debounced, cancellable lookup; only the answer for the current text is shown.
  useEffect(() => {
    lookupVersion.current += 1;
    if (readOnly || query.length < minQueryLength) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setMoreState(null);
      const lookup = directory.suggestPage
        ? directory.suggestPage(query, controller.signal, 0)
        : directory.suggest(query, controller.signal).then((items) => ({ items, nextOffset: null }));
      lookup.then(
        (page) => {
          if (controller.signal.aborted) return;
          setResult({ query, directory, ...page });
          setActive(page.items.length > 0 ? 0 : -1);
        },
        () => {
          if (!controller.signal.aborted) setResult({ query, directory, items: [], nextOffset: null, failed: true });
        },
      );
    }, debounceMs);
    return () => {
      clearTimeout(timer);
      controller.abort();
      lookupVersion.current += 1;
    };
  }, [query, directory, readOnly, debounceMs, minQueryLength, retryKey]);

  useEffect(() => {
    if (open && active >= 0) document.getElementById(`${listId}-${active}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open, listId]);

  const loadMore = async () => {
    if (!currentResult || result.nextOffset === null || !directory.suggestPage || moreLoading) return;
    const version = lookupVersion.current;
    const controller = new AbortController();
    const pending = { query, directory };
    setMoreState(pending);
    try {
      const page = await directory.suggestPage(query, controller.signal, result.nextOffset);
      if (version !== lookupVersion.current) return;
      setResult((previous) => ({ query, directory, ...page, items: [...previous.items, ...page.items], failed: false }));
    } catch {
      if (version === lookupVersion.current) setResult((previous) => ({ ...previous, failed: true }));
    } finally {
      setMoreState((current) => current === pending ? null : current);
    }
  };

  const focusInput = () => inputRef.current?.focus();
  const focusChip = (index: number) => {
    const r = value[index];
    if (r) chipRefs.current.get(r.id)?.focus();
    else focusInput();
  };

  const add = useCallback(
    (added: readonly Recipient[]) => {
      const seen = new Set(value.filter((r) => r.email).map((r) => r.email.toLowerCase()));
      const fresh = added.filter((r) => {
        if (selectionOnly && (!r.key || r.status !== 'resolved' || r.source === 'external')) return false;
        if (!r.email) return true;
        const key = r.email.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      // A single-choice field replaces its choice instead of refusing a second one.
      if (selectionOnly && maxRecipients === 1) { if (fresh.length > 0) onChange(fresh.slice(0, 1)); return; }
      if (maxRecipients !== undefined) fresh.splice(Math.max(0, maxRecipients - value.length));
      if (fresh.length > 0) onChange([...value, ...fresh]);
    },
    [onChange, value, selectionOnly, maxRecipients],
  );

  const closeList = () => {
    setOpen(false);
    setActive(-1);
  };

  /** Turns the typed text into chips. Returns true if something was committed. */
  const commitText = (): boolean => {
    if (selectionOnly) return false;
    const parsed = parseRecipientList(text);
    if (parsed.length === 0) return false;
    add(parsed.map(recipientFromParsed));
    setText('');
    closeList();
    return true;
  };

  const pick = (entry: PersonEntry) => {
    // Addressless groups must carry a fully resolved expansion. No empty/synthetic group chip reaches compose.
    if (entry.kind === 'group' && !entry.email) {
      if (!entry.members?.length || entry.members.some(member => !isValidEmail(member.email))) return;
      add(entry.members.map(recipientFromEntry));
    } else add([recipientFromEntry(entry)]);
    setText('');
    closeList();
    focusInput();
  };

  const remove = (r: Recipient, focusAfter: 'previous' | 'input' = 'input') => {
    const index = value.findIndex((x) => x.id === r.id);
    onChange(value.filter((x) => x.id !== r.id));
    if (focusAfter === 'previous' && index > 0) requestAnimationFrame(() => focusChip(index - 1));
    else requestAnimationFrame(focusInput);
  };

  const edit = (r: Recipient) => {
    if (readOnly) return;
    onChange(value.filter((x) => x.id !== r.id));
    setText((current) => (current ? `${current}; ${recipientText(r)}` : recipientText(r)));
    requestAnimationFrame(focusInput);
  };

  const replace = (r: Recipient, by: readonly Recipient[]) => {
    const index = value.findIndex((x) => x.id === r.id);
    if (index < 0) return;
    onChange([...value.slice(0, index), ...by, ...value.slice(index + 1)]);
  };

  const runCheckNames = async () => {
    if (selectionOnly) return;
    const typed = parseRecipientList(text).map(recipientFromParsed);
    setText('');
    closeList();
    const snapshot = [...value, ...typed];
    const generation = resolutionGeneration.current;
    currentValue.current = snapshot;
    onChange(snapshot);
    let checked: Recipient[];
    try { checked = await checkNames(snapshot, directory); }
    catch { return; }
    if (generation !== resolutionGeneration.current) return;
    // Resolve only chips that still exist unchanged; never resurrect removed chips or overwrite new edits.
    const original = new Map(snapshot.map((r) => [r.id, r]));
    const resolved = new Map(checked.map((r) => [r.id, r]));
    onChange(currentValue.current.map((r) => original.get(r.id) === r ? resolved.get(r.id) ?? r : r));
  };

  const removeSuggestion = (entry: PersonEntry) => {
    if (!directory.removeSuggestion) return;
    setSuggestions((list) => list.filter((s) => s.key !== entry.key));
    void directory.removeSuggestion(entry);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const listOpen = open && visible.length > 0;
    const highlighted = listOpen && active >= 0 ? visible[active] : undefined;
    if (isCheckNames(event.nativeEvent)) {
      event.preventDefault();
      event.stopPropagation();
      void runCheckNames();
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (visible.length === 0) return;
        event.preventDefault();
        setOpen(true);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((i) => (i + step + visible.length) % visible.length);
        return;
      }
      case 'Enter':
        // Never submits the form from a recipient field.
        event.preventDefault();
        if (highlighted) pick(highlighted);
        else commitText();
        return;
      case 'Tab':
        if (event.shiftKey) return;
        if (highlighted) {
          event.preventDefault();
          pick(highlighted);
        } else {
          commitText();
        }
        return;
      case ';':
      case ',': {
        // Inside quotes or <…> the separator is part of the text.
        const quotes = (text.match(/"/g) ?? []).length % 2 === 1;
        if (quotes || text.lastIndexOf('<') > text.lastIndexOf('>')) return;
        event.preventDefault();
        commitText();
        return;
      }
      case 'Escape':
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          closeList();
        }
        return;
      case 'Delete':
        if (highlighted?.source === 'recent' && directory.removeSuggestion) {
          event.preventDefault();
          removeSuggestion(highlighted);
        }
        return;
      case 'Backspace':
      case 'ArrowLeft': {
        const input = event.currentTarget;
        if (input.selectionStart === 0 && input.selectionEnd === 0 && value.length > 0) {
          event.preventDefault();
          focusChip(value.length - 1);
        }
        return;
      }
      default:
        return;
    }
  };

  const onChipKeyDown = (event: KeyboardEvent<HTMLButtonElement>, r: Recipient, index: number) => {
    if (isCheckNames(event.nativeEvent)) {
      event.preventDefault();
      event.stopPropagation();
      void runCheckNames();
      return;
    }
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        focusChip(Math.max(0, index - 1));
        return;
      case 'ArrowRight':
        event.preventDefault();
        focusChip(index + 1);
        return;
      case 'Home':
        event.preventDefault();
        focusChip(0);
        return;
      case 'End':
        event.preventDefault();
        focusInput();
        return;
      case 'Backspace':
      case 'Delete':
        if (readOnly) return;
        event.preventDefault();
        remove(r, event.key === 'Backspace' ? 'previous' : 'input');
        return;
      case 'F2':
        event.preventDefault();
        edit(r);
        return;
      case 'Enter':
        // Ambiguous chips open their candidate menu (Radix trigger); others edit.
        if (r.status === 'ambiguous') return;
        event.preventDefault();
        edit(r);
        return;
      default:
        return;
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (selectionOnly) return;
    const pasted = event.clipboardData.getData('text/plain');
    const parsed = parseRecipientList(pasted);
    if (parsed.length > 1 || (parsed.length === 1 && parsed[0]?.email !== undefined && /[;,\n<]/.test(pasted))) {
      event.preventDefault();
      add(parsed.map(recipientFromParsed));
      setText('');
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    setDragOver(false);
    const raw = event.dataTransfer.getData(DRAG_TYPE);
    if (!raw || readOnly || selectionOnly) return;
    event.preventDefault();
    const { field: from, recipient } = JSON.parse(raw) as { field: string; recipient: Recipient };
    if (from === field) return;
    add([recipient]);
    onDropRecipient?.(recipient, from);
  };

  const showList = open && !readOnly && query.length >= minQueryLength;
  const activeId = showList && active >= 0 && visible[active] ? `${listId}-${active}` : undefined;

  return (
    <div
      className={cn(
        'relative flex min-h-control w-full min-w-0 flex-1 flex-wrap items-center gap-1 rounded-md border border-input bg-transparent px-1.5 py-1 text-sm shadow-sm transition-colors focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 dark:bg-input/30',
        invalid && 'border-destructive ring-destructive/20',
        dragOver && 'border-ring bg-accent/50',
        readOnly && 'bg-muted/40',
      )}
      onClick={(event) => {
        if (event.target === event.currentTarget) focusInput();
      }}
      onDragOver={(event) => {
        if (readOnly || selectionOnly || !event.dataTransfer.types.includes(DRAG_TYPE)) return;
        event.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      onBlur={(event) => {
        // Leaving the field commits the typed text (Outlook); clicks in the list keep focus.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        if (!readOnly) commitText();
        closeList();
      }}
    >
      <ul aria-label={label} className="contents">
        {value.map((r, index) => (
          <li key={r.id} className="contents">
            <RecipientChip
              recipient={r}
              labels={labels}
              readOnly={readOnly}
              field={field}
              tabbable={readOnly && index === 0}
              photoUrl={r.email && r.status === 'resolved' && r.source !== 'external' ? directory.photoUrl?.(r.email) : undefined}
              chipRef={(el) => {
                if (el) chipRefs.current.set(r.id, el);
                else chipRefs.current.delete(r.id);
              }}
              onKeyDown={(event) => onChipKeyDown(event, r, index)}
              onEdit={() => edit(r)}
              onRemove={() => remove(r)}
              onChoose={(entry) => replace(r, [recipientFromEntry(entry)])}
              onExpand={
                r.kind === 'group' && directory.expandGroup && !readOnly
                  ? async () => {
                      const members = (await directory.expandGroup?.(r)) ?? [];
                      if (members.length > 0) replace(r, members.map(recipientFromEntry));
                    }
                  : undefined
              }
            />
          </li>
        ))}
      </ul>
      {!readOnly && (
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy ?? ariaDescribedBy}
          aria-label={label}
          autoComplete="off"
          autoFocus={autoFocus}
          spellCheck={false}
          placeholder={value.length === 0 ? placeholder : undefined}
          value={text}
          onFocus={() => { if (minQueryLength === 0) setOpen(true); }}
          onChange={(event) => {
            if (selectionOnly && maxRecipients === 1 && value.length > 0) onChange([]);
            setText(event.target.value);
            setOpen(true);
          }}
          onKeyDown={onInputKeyDown}
          onPaste={onPaste}
          className={`h-6 flex-1 bg-transparent px-1 outline-none placeholder:text-muted-foreground ${selectionOnly && maxRecipients === 1 && value.length > 0 ? 'min-w-4' : 'min-w-24'}`}
        />
      )}
      {showList && (
        <div
          id={listId}
          role="listbox"
          aria-label={labels.suggestions}
          // Keep the focus in the input while clicking an entry.
          onMouseDown={(event) => event.preventDefault()}
          className="absolute top-full right-0 left-0 z-50 mt-1 max-h-80 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          {currentResult && result.failed && <div role="alert" className="px-2 py-1.5 text-xs text-destructive">{labels.lookupFailed ?? ui.errorTitle}<Button variant="ghost" size="sm" onClick={() => result.nextOffset !== null ? void loadMore() : setRetryKey((key) => key + 1)}>{labels.retry ?? ui.retry}</Button></div>}
          {visible.length === 0 && !(currentResult && result.failed) ? (
            <p role="status" className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
              {loading && <LoaderCircleIcon aria-hidden className="size-3.5 animate-spin motion-reduce:animate-none" />}
              {loading ? labels.searching : labels.noMatches}
            </p>
          ) : (
            visible.map((s, index) => (
              <div
                key={s.key}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={index === active}
                onClick={() => pick(s)}
                onMouseMove={() => setActive(index)}
                className={cn('group flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5', index === active && 'bg-accent text-accent-foreground')}
              >
                <PersonAvatar name={s.name} email={s.email} group={s.kind === 'group'} photoUrl={directory.photoUrl?.(s.email)} size="md" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{s.name || s.email}</span>
                  {s.name && s.name !== s.email ? <span className="truncate text-xs text-muted-foreground">{s.email}</span> : null}
                </span>
                {s.kind === 'group' && <Badge variant="muted">{labels.group}</Badge>}
                <Badge variant={s.source === 'recent' ? 'secondary' : 'outline'}>{labels.sources[s.source]}</Badge>
                {s.source === 'recent' && directory.removeSuggestion && (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={fill(labels.removeSuggestion, s.name || s.email)}
                    onClick={(event) => {
                      event.stopPropagation();
                      removeSuggestion(s);
                    }}
                    className="rounded-sm p-0.5 text-muted-foreground opacity-60 hover:bg-muted hover:opacity-100 group-aria-selected:opacity-100"
                  >
                    <XIcon className="size-3.5" />
                  </button>
                )}
              </div>
            ))
          )}
          {currentResult && result.nextOffset !== null && <Button variant="ghost" size="sm" disabled={moreLoading} onClick={() => void loadMore()}>{moreLoading ? labels.searching : labels.loadMore}</Button>}
          {currentResult && result.capped && <p role="status" className="px-2 py-1.5 text-xs text-muted-foreground">{labels.searchLimit}</p>}
        </div>
      )}
    </div>
  );
}

interface RecipientChipProps {
  recipient: Recipient;
  labels: RecipientInputLabels;
  readOnly: boolean;
  field: string;
  /** In the tab order (first chip of a read-only field, which has no text input). */
  tabbable: boolean;
  photoUrl?: string | undefined;
  chipRef: (el: HTMLButtonElement | null) => void;
  onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  onEdit: () => void;
  onRemove: () => void;
  onChoose: (entry: PersonEntry) => void;
  onExpand?: (() => Promise<void>) | undefined;
}

function statusText(r: Recipient, labels: RecipientInputLabels): string | undefined {
  const name = recipientLabel(r);
  switch (r.status) {
    case 'unresolved':
      return fill(labels.unresolved, name);
    case 'ambiguous':
      return fill(labels.ambiguous, name);
    case 'invalid':
      return fill(labels.invalid, name);
    case 'pending':
      return fill(labels.pending, name);
    default:
      return undefined;
  }
}

function RecipientChip({ recipient: r, labels, readOnly, field, tabbable, photoUrl, chipRef, onKeyDown, onEdit, onRemove, onChoose, onExpand }: RecipientChipProps) {
  const name = recipientLabel(r);
  const problem = statusText(r, labels);
  const flagged = r.status === 'unresolved' || r.status === 'ambiguous' || r.status === 'invalid';
  const tooltip = problem ?? (r.email && r.name && r.name !== r.email ? r.email : undefined);

  const main = (
    <button
      ref={chipRef}
      type="button"
      tabIndex={tabbable ? 0 : -1}
      data-status={r.status}
      aria-label={problem ? `${name}, ${problem}` : r.email && r.name ? `${r.name} <${r.email}>` : name}
      draggable={!readOnly}
      onDragStart={(event) => {
        event.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ field, recipient: r }));
        event.dataTransfer.effectAllowed = 'move';
      }}
      onDoubleClick={onEdit}
      onKeyDown={onKeyDown}
      className={cn(
        'flex max-w-64 min-w-0 items-center gap-1.5 rounded-full py-0.5 pr-2 pl-0.5 text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50',
        flagged ? 'text-destructive' : r.status === 'pending' && 'italic',
      )}
    >
      {flagged ? (
        <AlertTriangleIcon aria-hidden className="ml-1 size-4 shrink-0" />
      ) : (
        <PersonAvatar name={r.name} email={r.email || name} group={r.kind === 'group'} photoUrl={photoUrl} size="xs" />
      )}
      <span className={cn('truncate', flagged && 'underline decoration-wavy underline-offset-2')}>{name}</span>
    </button>
  );

  return (
    <span
      data-slot="recipient-chip"
      className={cn(
        'inline-flex max-w-full items-center rounded-full border bg-secondary text-secondary-foreground',
        flagged && 'border-destructive/50 bg-destructive/10',
        r.kind === 'group' && !flagged && 'bg-accent',
      )}
    >
      {r.status === 'ambiguous' && r.candidates && r.candidates.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{main}</DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-64">
            <DropdownMenuLabel>{labels.chooseCandidate}</DropdownMenuLabel>
            {r.candidates.map((c) => (
              <DropdownMenuItem key={c.key} onSelect={() => onChoose(c)}>
                <PersonAvatar name={c.name} email={c.email} size="sm" />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate">{c.name || c.email}</span>
                  <span className="truncate text-xs text-muted-foreground">{c.email}</span>
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>{main}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        main
      )}
      {onExpand && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={fill(labels.expandGroup, name)}
          onClick={() => void onExpand()}
          className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <PlusIcon className="size-3.5" />
        </button>
      )}
      {!readOnly && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={fill(labels.remove, name)}
          onClick={onRemove}
          className="mr-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <XIcon className="size-3.5" />
        </button>
      )}
    </span>
  );
}
